import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { renderTextArea } from '../../../shared/formComponents';
import Tooltip from '../../../../../shared/components/widgets/Tooltip';

/**
 * Reusable geo-targeting component for Google/Meta/TikTok platforms
 * TikTok/Meta: Countries always visible (all modes/views)
 * Google: Countries only in Limitless View, states/cities/postal in Advanced+Limitless
 */
const GeoTargetingFields = ({
  setupFlow,
  agencyInternal,
  platform = 'google',
  supportsCities = true,
  supportsStates = true,
  supportsPostalCodes = true,
}) => {
  const isAdvancedLimitless = setupFlow?.value === 'advanced' && agencyInternal?.value;
  const isGooglePlatform = platform === 'google';

  // TikTok/Meta: always show countries. Google: only in Limitless View
  const showCountries = platform === 'tiktok' || platform === 'meta' || agencyInternal?.value;

  return (
    <>
      {/* Countries - Always visible for TikTok/Meta, Limitless-only for Google */}
      {showCountries && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            {isGooglePlatform ? 'Countries (Optional)' : 'Countries'}{' '}
            <Tooltip
              id="geoCountries"
              msg={isGooglePlatform
                ? 'Enter comma-separated Google geo target constants (e.g., geoTargetConstants/2840)'
                : 'Enter comma-separated 2-character country codes (e.g., US, CA, MX)'
              }
              placement="top"
            />
          </span>
          <div className="form__form-group-field">
            <Field
              name="geoCountries"
              component={renderTextArea}
              placeholder={isGooglePlatform ? 'Geo Target Constant for Country' : 'US, CA, MX'}
            />
          </div>
        </div>
      )}

      {/* States - Advanced + Limitless only */}
      {isAdvancedLimitless && supportsStates && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            States (Optional) <Tooltip id="geoStates" msg="Enter comma-separated state or region codes (e.g., geoTargetConstants/21176)" placement="top" />
          </span>
          <div className="form__form-group-field">
            <Field
              name="geoStates"
              component={renderTextArea}
              placeholder="Geo Target Constant for State"
            />
          </div>
        </div>
      )}

      {/* Cities - Advanced + Limitless only */}
      {isAdvancedLimitless && supportsCities && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            Cities (Optional) <Tooltip id="geoCities" msg="Enter comma-separated city names (e.g., geoTargetConstants/1016367)" placement="top" />
          </span>
          <div className="form__form-group-field">
            <Field
              name="geoCities"
              component={renderTextArea}
              placeholder="Geo Target Constant for City"
            />
          </div>
        </div>
      )}

      {/* Postal Codes - Advanced + Limitless only */}
      {isAdvancedLimitless && supportsPostalCodes && (
        <div className="form__form-group">
          <span className="form__form-group-label">
            Postal Codes (Optional) <Tooltip id="geoPostalCodes" msg="Enter comma-separated postal codes (e.g., geoTargetConstants/9055331)" placement="top" />
          </span>
          <div className="form__form-group-field">
            <Field
              name="geoPostalCodes"
              component={renderTextArea}
              placeholder="Geo Target Constant for Postal Code"
            />
          </div>
        </div>
      )}
    </>
  );
};

GeoTargetingFields.propTypes = {
  setupFlow: PropTypes.shape({
    value: PropTypes.string,
  }),
  agencyInternal: PropTypes.shape({
    value: PropTypes.bool,
  }),
  platform: PropTypes.oneOf(['google', 'meta', 'tiktok']),
  supportsCities: PropTypes.bool,
  supportsStates: PropTypes.bool,
  supportsPostalCodes: PropTypes.bool,
};

GeoTargetingFields.defaultProps = {
  setupFlow: null,
  agencyInternal: null,
  platform: 'google',
  supportsCities: true,
  supportsStates: true,
  supportsPostalCodes: true,
};

export default GeoTargetingFields;
